import './login-drawer.css';
import { verifyLoginOtp } from '~/utils/verify-login-otp';
import { syncCustomerPricing } from '~/utils/customer-login-sync';
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import LazyPhoneInput from '~/components/LazyPhoneInput';
import { Link } from '@remix-run/react';
import { useFocusTrap } from '~/hooks/use-focus-trap';
import { klaviyo, trackLogin } from '~/utils/analytics';
import { startOAuthLogin } from '~/utils/oauth';

// Helper function to convert a phone number string to an E.164–like format
function formatPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  // If 10 digits are provided, assume US number and prepend "1"
  if (digits.length === 10) {
    return '1' + digits;
  }
  return digits;
}

interface LoginFormProps {
  onLoginClick?: () => void;
}

export default function LoginForm({ onLoginClick }: LoginFormProps = {}) {
  // Modal state (login button only shows when modal is closed)
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Step 1: login details; Step 2: OTP verification
  const [step, setStep] = useState(1);
  // Login method: 'email' or 'phone'
  const [loginMethod, setLoginMethod] = useState<'email' | 'phone'>('email');
  // For showing the spinner during verification
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const verifyingOtp = useRef(false);
  // For controlling resend code state
  const [isResending, setIsResending] = useState(false);
  // Capture input values
  const [emailValue, setEmailValue] = useState('');
  const [phoneValue, setPhoneValue] = useState('');
  // For storing OTP verification errors
  const [otpError, setOtpError] = useState('');
  // For storing any alert errors as a red error box
  const [lookupError, setLookupError] = useState<React.ReactNode>(null);
  // For screen reader announcements
  const [announcement, setAnnouncement] = useState('');

  // Create refs for the 6 OTP input fields
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const loginModalRef = useFocusTrap(isModalOpen);
  const previousFocusElement = useRef<HTMLElement | null>(null);

  // Effect to manage focus when modal opens/closes
  useEffect(() => {
    if (isModalOpen) {
      previousFocusElement.current = document.activeElement as HTMLElement;
    } else if (previousFocusElement.current) {
      previousFocusElement.current.focus();
    }
  }, [isModalOpen]);

  // Announce when login modal opens/closes
  useEffect(() => {
    if (isModalOpen && step === 1) {
      // Clear announcement first
      setAnnouncement('');
      // Set announcement with longer delay to override dialog announcement
      const timer = setTimeout(() => {
        setAnnouncement(
          'Join Catalyst Rewards Club modal opened. Sign up to earn points and get exclusive deals. Choose email or phone to continue.',
        );
      }, 300);
      return () => clearTimeout(timer);
    } else if (isModalOpen && step === 2) {
      setAnnouncement('');
      const timer = setTimeout(() => {
        setAnnouncement(
          'Verification code step. Enter the 6-digit code sent to your email or phone.',
        );
      }, 200);
      return () => clearTimeout(timer);
    } else if (!isModalOpen) {
      setAnnouncement('Rewards club modal closed.');
    }
  }, [isModalOpen, step]);

  // Listen for custom close event from focus trap
  useEffect(() => {
    const handleClose = () => setIsModalOpen(false);
    document.addEventListener('close-focus-trap', handleClose);
    return () => document.removeEventListener('close-focus-trap', handleClose);
  }, []);

  useEffect(() => {
    const handleLoginTrigger = () => {
      setIsModalOpen(true);
    };
    window.addEventListener('login:trigger', handleLoginTrigger);
    return () => window.removeEventListener('login:trigger', handleLoginTrigger);
  }, []);

  // When modal opens, reset state
  useEffect(() => {
    if (isModalOpen) {
      setStep(1);
      setIsLoggingIn(false);
      setIsResending(false);
      setLoginMethod('email');
      setEmailValue('');
      setPhoneValue('');
      setOtpError('');
      setLookupError(null);
      otpRefs.current.forEach((el) => {
        if (el) el.value = '';
      });
    }
  }, [isModalOpen]);

  useEffect(() => {
    if (step === 2) {
      setTimeout(() => {
        const firstInput = otpRefs.current[0];
        if (firstInput) {
          firstInput.focus();
          firstInput.click(); // simulate a user tap to bring up the keyboard on iOS
          firstInput.select();
        }
      }, 100);
    }
  }, [step]);

  // --- Step 1: Request OTP ---
  const handleLoginSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoggingIn(true); // Show spinner
    setLookupError(null); // Clear previous error messages

    let payload;
    if (loginMethod === 'email') {
      payload = { email: emailValue.trim().toLowerCase(), create_user: false };
    } else {
      const formattedPhone = formatPhone(phoneValue);
      payload = { phone: formattedPhone, create_user: false };
    }

    try {
      const response = await fetch('https://budflow.com/catalyst/auth/otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok || data.error_code) {
        if (/signups not allowed for otp/i.test(data.msg || data.message || '') || data.error_code === 'signup_disabled') {
          // Build the lookup URL based on the login method.
          let lookupUrl = '';
          if (loginMethod === 'phone') {
            const formattedPhone = formatPhone(phoneValue);
            lookupUrl = `/api/getloyalty?lookupType=phone&value=${formattedPhone}`;
          } else {
            lookupUrl = `/api/getloyalty?lookupType=email&value=${encodeURIComponent(emailValue)}`;
          }
          // Call the lookup endpoint
          const lookupResponse = await fetch(lookupUrl);
          const lookupData = await lookupResponse.json();

          if (lookupData.success) {
            // The server independently verifies Alpine identity before reconciling.
            const budflowPayload = {
              lookupType: loginMethod,
              value: loginMethod === 'phone' ? formatPhone(phoneValue) : emailValue,
            };

            const budflowResponse = await fetch('/api/budflowuser', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(budflowPayload),
            });
            const budflowResult = await budflowResponse.json();

            if (budflowResponse.ok && budflowResult.success) {
              // Re-trigger OTP request using the same payload as before
              setIsLoggingIn(true);
              const otpRetriggerResponse = await fetch('https://budflow.com/catalyst/auth/otp', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
              });
              const otpRetriggerData = await otpRetriggerResponse.json();
              setIsLoggingIn(false);
              if (otpRetriggerResponse.ok && !otpRetriggerData.error_code) {
                setStep(2);
              } else {
                setLookupError(
                  <div className="mb-4 rounded bg-red-100 p-2 text-red-700">
                    {otpRetriggerData.msg || 'Failed to retrigger OTP'}
                  </div>,
                );
              }
            } else {
              setLookupError(
                <div className="mb-4 rounded bg-red-100 p-2 text-red-700">
                  {budflowResult.error || 'Unable to prepare your login account.'}
                </div>,
              );
            }
          } else {
            console.error('Lookup failed:', lookupData);
            setLookupError(
              <div className="mb-4 rounded bg-red-100 p-2 text-red-700">
                We couldn't find an account with those details,&nbsp;
                <Link to="/register" className="underline" onClick={() => setIsModalOpen(false)}>
                  Register
                </Link>
              </div>,
            );
          }
        } else {
          setLookupError(
            <div className="mb-4 rounded bg-red-100 p-2 text-red-700">
              {data.msg || 'Failed to send OTP'}
            </div>,
          );
        }
      } else {
        setStep(2);
      }
    } catch (error) {
      console.error(error);
      setLookupError(
        <div className="mb-4 rounded bg-red-100 p-2 text-red-700">Failed to send OTP</div>,
      );
    } finally {
      setIsLoggingIn(false);
    }
  };

  // --- Step 2: Verify OTP ---
  const verifyOtp = () => {
    if (verifyingOtp.current || isLoggingIn) return;
    // Combine the OTP digits into one string
    const token = otpRefs.current.map((el) => (el ? el.value : '')).join('');
    if (!/^\d{6}$/.test(token)) {
      setOtpError('Enter the six-digit code.');
      return;
    }
    verifyingOtp.current = true;
    setIsLoggingIn(true);
    setOtpError('');
    let verifyPayload;
    if (loginMethod === 'email') {
      verifyPayload = {
        type: 'email',
        token,
        redirect_to: 'https://catalyst-cannabis.co',
        email: emailValue.trim().toLowerCase(),
      };
    } else {
      // Convert phone value to E.164–like format
      const formattedPhone = formatPhone(phoneValue);
      verifyPayload = {
        type: 'sms',
        token,
        redirect_to: 'https://catalyst-cannabis.co',
        phone: formattedPhone,
      };
    }

    verifyLoginOtp(verifyPayload)
      .then(async (data) => {
        // Success: store user.id in cookie and local storage
        if (data.access_token) await syncCustomerPricing(data.access_token);
        // Phase 6: persist the GoTrue session tokens so SSR loaders can read
        // access_token (→ personalised product_search) and the client hook can
        // refresh before the ~1h expiry. Cookies (SSR) + localStorage (client).
        if (data.access_token) {
          const yr = 60 * 60 * 24 * 365;
          const expiresIn = typeof data.expires_in === 'number' ? data.expires_in : 3600;
          const expiresAt =
            typeof data.expires_at === 'number'
              ? data.expires_at
              : Math.floor(Date.now() / 1000) + expiresIn;
          document.cookie = `access_token=${data.access_token}; path=/; max-age=${expiresIn}; SameSite=Lax`;
          document.cookie = `token_expires_at=${expiresAt}; path=/; max-age=${yr}; SameSite=Lax`;
          localStorage.setItem('access_token', data.access_token);
          localStorage.setItem('token_expires_at', String(expiresAt));
          if (data.refresh_token) {
            document.cookie = `refresh_token=${data.refresh_token}; path=/; max-age=${yr}; SameSite=Lax`;
            localStorage.setItem('refresh_token', data.refresh_token);
          }
        }
        const userId = data.user && data.user.id;
        if (userId) {
          const oneYearInSeconds = 60 * 60 * 24 * 365;
          localStorage.setItem('catalystUserId', userId);
          document.cookie = `catalystUserId=${userId}; path=/; max-age=${oneYearInSeconds}`;
        }
        // Also store additional fields from app_metadata
        if (data.user && data.user.app_metadata) {
          const { alpine_iq_id, email, first_name, last_name, dob, phone } =
            data.user.app_metadata;
          const oneYearInSeconds = 60 * 60 * 24 * 365;
          if (alpine_iq_id) {
            localStorage.setItem('alpine_iq_id', alpine_iq_id);
            document.cookie = `alpine_iq_id=${encodeURIComponent(alpine_iq_id)}; path=/; max-age=${oneYearInSeconds}`;
          }
          if (email) {
            localStorage.setItem('email', email);
            document.cookie = `email=${encodeURIComponent(email)}; path=/; max-age=${oneYearInSeconds}`;

            // Add Thoughtmetric identify call when email is available
            if (window.thoughtmetric) {
              window.thoughtmetric('identify', email, {
                email: email,
              });
            }

            // 🔥 Klaviyo Enhanced Analytics - User identification
            klaviyo.identify({
              email: email,
              phone: phone,
              first_name: first_name,
              last_name: last_name,
              dob: dob,
            });
          }

          // 🔥 Enhanced Analytics - Login tracking (always track successful login)
          klaviyo.login('OTP');
          trackLogin('OTP');
          if (first_name) {
            localStorage.setItem('first_name', first_name);
            document.cookie = `first_name=${encodeURIComponent(first_name)}; path=/; max-age=${oneYearInSeconds}`;
          }
          if (last_name) {
            localStorage.setItem('last_name', last_name);
            document.cookie = `last_name=${encodeURIComponent(last_name)}; path=/; max-age=${oneYearInSeconds}`;
          }
          if (dob) {
            localStorage.setItem('dob', dob);
            document.cookie = `dob=${encodeURIComponent(dob)}; path=/; max-age=${oneYearInSeconds}`;
          }
          if (phone) {
            localStorage.setItem('phone', phone);
            document.cookie = `phone=${encodeURIComponent(phone)}; path=/; max-age=${oneYearInSeconds}`;
          }
        }
        // 🔒 SIMPLE PRESERVE: Try to save modal state before reload
        // Accepts brief flash - if it fails, modal closes (acceptable UX)
        try {
          // Try to save modal state directly as fallback (in case event listener isn't ready)
          // Check if product modal wrapper exists in DOM
          const modalWrapper = document.getElementById('modal-wrapper');
          let productId = null;

          // Get product ID from data attribute (we added this to modal-wrapper)
          if (modalWrapper) {
            productId = modalWrapper.getAttribute('data-product-id');
          }

          // Fallback: Try to extract from product modal link if data attribute not available
          if (!productId) {
            const productModal = document.getElementById('product-modal');
            if (productModal) {
              const productLink = productModal.querySelector('a[href*="/product/"]');
              if (productLink) {
                const href = productLink.getAttribute('href');
                const match = href?.match(/\/product\/([^\/]+)/);
                if (match) {
                  productId = match[1];
                }
              }
            }
          }

          if (productId && typeof localStorage !== 'undefined') {
            try {
              const modalState = {
                isProductModalOpen: true,
                currentProductId: productId,
                timestamp: Date.now(),
              };
              localStorage.setItem('catalyst_product_modal_preserve', JSON.stringify(modalState));
            } catch (directSaveError) {
              // Ignore - non-critical
              console.warn('Could not save modal state (non-critical):', directSaveError);
            }
          }

          // Also try the event-based approach
          try {
            window.dispatchEvent(new CustomEvent('preserveModalState'));
          } catch (eventError) {
            // Non-critical - continue with reload
            console.warn(
              'Could not dispatch preserveModalState event (non-critical):',
              eventError,
            );
          }

          // Short delay to let state save, then reload
          // Always reload regardless of preservation success
          setTimeout(() => {
            try {
              window.dispatchEvent(new Event('user:login'));
            } catch (loginEventError) {
              // Non-critical - continue with reload
              console.warn(
                'Could not dispatch user:login event (non-critical):',
                loginEventError,
              );
            }
            window.location.reload();
          }, 200); // Increased to 200ms to ensure state is saved
          return;
        } catch (error) {
          // If preservation fails, still reload (modal will close, which is acceptable)
          console.warn('Modal state preservation failed (non-critical):', error);
          window.dispatchEvent(new Event('user:login'));
          window.location.reload();
        }
      })
      .catch((error) => {
        setIsLoggingIn(false);
        console.error(error);
        setOtpError(error instanceof Error ? error.message : 'OTP verification failed');
      })
      .finally(() => {
        verifyingOtp.current = false;
        setIsLoggingIn(false);
      });
  };

  const handleOtpSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    verifyOtp();
  };

  // --- Updated Resend Code Functionality ---
  const handleResendCode = async () => {
    setIsResending(true);
    setLookupError(null); // Clear previous errors
    let payload;
    if (loginMethod === 'email') {
      payload = { email: emailValue.trim().toLowerCase(), create_user: false };
    } else {
      const formattedPhone = formatPhone(phoneValue);
      payload = { phone: formattedPhone, create_user: false };
    }
    try {
      const response = await fetch('https://budflow.com/catalyst/auth/otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok || data.error_code) {
        setLookupError(
          <div className="mb-4 rounded bg-red-100 p-2 text-red-700">
            {data.msg || 'Failed to resend OTP'}
          </div>,
        );
      } else {
        setLookupError(
          <div className="mb-4 rounded bg-green-100 p-2 text-green-700">
            OTP resent successfully!
          </div>,
        );
      }
    } catch (error) {
      console.error(error);
      setLookupError(
        <div className="mb-4 rounded bg-red-100 p-2 text-red-700">Failed to resend OTP</div>,
      );
    }
    setIsResending(false);
  };

  // Allow going back from OTP to login details
  const handleBack = () => {
    setOtpError('');
    setStep(1);
  };

  // When a digit is entered, move focus to the next OTP field.
  // If all OTP fields are filled, auto-submit.
  const handleOtpInputChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const value = e.target.value;
    if (value.length === 1 && index < otpRefs.current.length - 1) {
      otpRefs.current[index + 1]?.focus();
    }

    const otp = otpRefs.current.map((el) => (el ? el.value : '')).join('');
    if (otp.length === 6) {
      verifyOtp(); // Auto-submit when 6 digits are entered
    }
  };

  // When backspace is pressed in an OTP field that is empty, move focus to the previous field.
  const handleOtpKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace') {
      if ((e.target as HTMLInputElement).value === '' && index > 0) {
        otpRefs.current[index - 1]?.focus();
        if (otpRefs.current[index - 1]) {
          otpRefs.current[index - 1]!.value = '';
        }
      }
    }
  };

  // When an OTP field is focused, clear its content for reentry and clear any error message.
  const handleOtpFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.value = '';
    setOtpError('');
  };

  // Close the modal if the overlay (background) is clicked.
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      setIsModalOpen(false);
    }
  };

  // Handle OTP paste: Spreads pasted OTP across all fields
  const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text').trim();
    if (pastedText.length === 6) {
      pastedText.split('').forEach((char, index) => {
        if (otpRefs.current[index]) {
          otpRefs.current[index]!.value = char;
        }
      });
      verifyOtp(); // Auto-submit after pasting
    }
  };

  return (
    <div className="font-magda text-wrap">
      {/* ARIA Live Region for Login Modal Announcements */}
      <div aria-live="assertive" aria-atomic="true" className="sr-only">
        {announcement}
      </div>
      {/* Render the login button only when the modal is closed */}
      <button
        onClick={() => {
          onLoginClick?.();
          setIsModalOpen(true);
        }}
        className="login-button hover:text-catYellow flex flex-col text-sm text-white transition duration-200 md:text-sm lg:underline"
        aria-label="Login to your account"
      >
        <svg
          className="mt-auto"
          width="25"
          height="25"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 512 512"
          aria-hidden="true"
        >
          <path
            d="M412.1 416.6C398.1 361.1 347.9 320 288 320l-64 0c-59.9 0-110.1 41.1-124.1 96.6C58 375.9 32 319 32 256C32 132.3 132.3 32 256 32s224 100.3 224 224c0 63-26 119.9-67.9 160.6zm-28.5 23.4C347.5 465.2 303.5 480 256 480s-91.5-14.8-127.7-39.9c4-49.3 45.3-88.1 95.7-88.1l64 0c50.4 0 91.6 38.8 95.7 88.1zM256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zm0-256a48 48 0 1 1 0-96 48 48 0 1 1 0 96zm-80-48a80 80 0 1 0 160 0 80 80 0 1 0 -160 0z"
            fill="white"
          ></path>
        </svg>
      </button>

      {isModalOpen &&
        step === 1 &&
        createPortal(
          <div
            ref={loginModalRef}
            id="login-dropdown"
            className="fixed inset-0 z-[200] flex items-end justify-center bg-black/40 backdrop-blur-sm md:items-center md:p-4"
            onClick={handleOverlayClick}
            role="dialog"
            aria-modal="true"
            aria-label="Join Catalyst Rewards Club"
          >
            <div className="catalyst-login-drawer relative max-h-[90dvh] w-full overflow-y-auto overscroll-contain rounded-t-2xl bg-white pb-[env(safe-area-inset-bottom)] shadow-2xl md:max-w-sm md:rounded-2xl md:pb-0">
              <h2 id="login-modal-title" className="sr-only">
                Join Catalyst Rewards Club
              </h2>

              {/* Close button */}
              <button
                onClick={() => setIsModalOpen(false)}
                className="absolute top-3 right-3 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/80 text-gray-500 transition-colors hover:bg-white hover:text-gray-700"
                aria-label="Close sign up modal"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>

              {/* Header with rewards icon */}
              <div className="px-6 pt-8 text-center">
                <img
                  src="/rewardsicon.webp"
                  alt="Catalyst Rewards"
                  className="mx-auto mb-4 w-28 object-contain"
                />
                <h3 className="text-xl font-bold text-gray-900">Welcome Back</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Sign in to your Catalyst Rewards account
                </p>
              </div>

              {/* Form area */}
              <div className="px-6 pt-5 pb-6">
                {/* Email / Phone toggle */}
                <div className="mb-5 flex rounded-lg bg-gray-100 p-1">
                  <button
                    type="button"
                    id="email-tab"
                    onClick={() => setLoginMethod('email')}
                    className={`flex-1 rounded-md px-4 py-2 text-sm font-semibold transition-all ${
                      loginMethod === 'email'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Email
                  </button>
                  <button
                    type="button"
                    id="phone-tab"
                    onClick={() => setLoginMethod('phone')}
                    className={`flex-1 rounded-md px-4 py-2 text-sm font-semibold transition-all ${
                      loginMethod === 'phone'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Phone
                  </button>
                </div>

                <form id="login-form" onSubmit={handleLoginSubmit}>
                  {loginMethod === 'email' && (
                    <div className="mb-4">
                      <label
                        htmlFor="email"
                        className="mb-1.5 block text-xs font-medium text-gray-500"
                      >
                        Email Address
                        <span aria-hidden="true" className="text-red-500">
                          {' '}
                          *
                        </span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={emailValue}
                        onChange={(e) => setEmailValue(e.target.value)}
                        className="w-full rounded-xl border-2 border-gray-200 px-4 py-3 text-sm text-black transition-colors focus:border-black focus:outline-none"
                        placeholder="you@example.com"
                        required
                      />
                    </div>
                  )}
                  {loginMethod === 'phone' && (
                    <div className="mb-4">
                      <label
                        htmlFor="phone"
                        className="mb-1.5 block text-xs font-medium text-gray-500"
                      >
                        Phone Number
                        <span aria-hidden="true" className="text-red-500">
                          {' '}
                          *
                        </span>
                      </label>
                      <LazyPhoneInput
                        placeholder="(555) 555-5555"
                        value={phoneValue}
                        onChange={(value) => setPhoneValue(value || '')}
                        defaultCountry="US"
                        id="phone"
                        name="phone"
                        className="w-full rounded-xl border-2 border-gray-200 px-4 py-3 text-sm text-black transition-colors focus:border-black focus:outline-none"
                        required
                      />
                    </div>
                  )}

                  {/* role="alert" so login errors (e.g. "no account found") are announced to
                    screen readers the moment they appear, not just shown visually. */}
                  {lookupError && <div role="alert">{lookupError}</div>}

                  <button
                    type="submit"
                    disabled={isLoggingIn}
                    className="bg-catalystblue w-full rounded-xl py-3.5 text-sm font-bold text-white transition-all hover:brightness-110 disabled:opacity-60"
                  >
                    {isLoggingIn ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                        <span>Sending code...</span>
                      </div>
                    ) : (
                      'Continue'
                    )}
                  </button>
                </form>

                {/* Social sign-in. GoTrue owns the whole round-trip: it bounces
                  to the provider and returns here with the session in the URL
                  hash, which magic-link-handler.tsx (mounted in _layout) picks
                  up — the same path the magic link already uses. */}
                <div className="mt-5">
                  <div className="relative" aria-hidden="true">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200" />
                    </div>
                    <div className="relative flex justify-center">
                      <span className="bg-white px-3 text-xs text-gray-500">or</span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => startOAuthLogin('google')}
                    className="mt-5 flex w-full items-center justify-center gap-3 rounded-xl border-2 border-gray-200 py-3.5 text-sm font-bold text-gray-900 transition-colors hover:border-gray-300 hover:bg-gray-50"
                    aria-label="Continue with Google"
                  >
                    <svg className="h-5 w-5" viewBox="0 0 18 18" aria-hidden="true">
                      <path
                        fill="#4285F4"
                        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"
                      />
                      <path
                        fill="#34A853"
                        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.34A9 9 0 0 0 9 18z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.94H.96a9 9 0 0 0 0 8.12l3.01-2.34z"
                      />
                      <path
                        fill="#EA4335"
                        d="M9 3.58c1.32 0 2.5.46 3.44 1.35l2.58-2.58C13.46.9 11.43 0 9 0A9 9 0 0 0 .96 4.94l3.01 2.34C4.68 5.16 6.66 3.58 9 3.58z"
                      />
                    </svg>
                    Continue with Google
                  </button>

                  {/* Apple's guidelines require their mark and one of their
                      approved labels, on black, white or outlined. */}
                  <button
                    type="button"
                    onClick={() => startOAuthLogin('apple')}
                    className="mt-3 flex w-full items-center justify-center gap-3 rounded-xl border-2 border-black bg-black py-3.5 text-sm font-bold text-white transition-colors hover:bg-gray-900"
                    aria-label="Continue with Apple"
                  >
                    <svg
                      className="h-5 w-5"
                      viewBox="0 0 814 1000"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.7 40.8-165.9 40.8s-105.6-57-155.5-127C46.7 790.7 0 663 0 541.8c0-194.4 126.4-297.5 250.8-297.5 66.1 0 121.2 43.4 162.7 43.4 39.5 0 101.1-46 176.3-46 28.5 0 130.9 2.6 198.3 99.2zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z" />
                    </svg>
                    Continue with Apple
                  </button>
                </div>

                <Link
                  to="/register"
                  className="mt-4 block rounded-xl border-2 border-gray-200 px-4 py-3 text-center text-sm transition-colors hover:border-gray-300 hover:bg-gray-50"
                  onClick={() => setIsModalOpen(false)}
                >
                  <span className="text-gray-500">New here?</span>{' '}
                  <span className="font-semibold text-gray-900">Create an account</span>
                </Link>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {isModalOpen &&
        step === 2 &&
        createPortal(
          <div
            ref={loginModalRef}
            id="otp-verification"
            className="fixed inset-0 z-[200] flex items-end justify-center bg-black/40 backdrop-blur-sm md:items-center md:p-4"
            onClick={handleOverlayClick}
            role="dialog"
            aria-modal="true"
            aria-labelledby="otp-modal-title"
          >
            <div className="catalyst-login-drawer relative max-h-[90dvh] w-full overflow-y-auto overscroll-contain rounded-t-2xl bg-white pb-[env(safe-area-inset-bottom)] shadow-2xl md:max-w-sm md:rounded-2xl md:pb-0">
              {/* Close button */}
              <button
                onClick={() => setIsModalOpen(false)}
                className="absolute top-3 right-3 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/80 text-gray-500 transition-colors hover:bg-white hover:text-gray-700"
                aria-label="Close verification modal"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>

              {/* Header */}
              <div className="px-6 pt-8 text-center">
                <img
                  src="/rewardsicon.webp"
                  alt="Catalyst Rewards"
                  className="mx-auto mb-4 w-24 object-contain"
                />
                <h2 id="otp-modal-title" className="text-xl font-bold text-gray-900">
                  Enter Your Code
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  We sent a 6-digit code to your {loginMethod === 'email' ? 'email' : 'phone'}
                </p>
              </div>

              <div className="px-6 pt-5 pb-6">
                {/* OTP error */}
                {otpError && (
                  <div
                    role="alert"
                    className="mb-4 rounded-xl bg-red-50 px-4 py-3 text-center text-sm text-red-600"
                  >
                    {otpError}
                  </div>
                )}
                {lookupError && <div role="alert">{lookupError}</div>}

                {isLoggingIn ? (
                  <div className="flex flex-col items-center justify-center py-8">
                    <div className="h-10 w-10 animate-spin rounded-full border-3 border-gray-200 border-t-black"></div>
                    <p className="mt-4 text-sm text-gray-500">Verifying...</p>
                  </div>
                ) : (
                  <form id="otp-form" onSubmit={handleOtpSubmit}>
                    <div className="mb-5 flex justify-center gap-2 sm:gap-3">
                      {Array.from({ length: 6 }).map((_, index) => (
                        <input
                          key={index}
                          type="tel"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength={1}
                          ref={(el) => (otpRefs.current[index] = el)}
                          onChange={(e) => handleOtpInputChange(e, index)}
                          onKeyDown={(e) => handleOtpKeyDown(e, index)}
                          onFocus={handleOtpFocus}
                          onPaste={handleOtpPaste}
                          autoComplete={index === 0 ? 'one-time-code' : 'off'}
                          className="h-14 min-w-0 max-w-11 flex-1 rounded-xl border-2 border-gray-200 bg-gray-50 text-center text-xl font-bold text-gray-900 transition-all focus:border-black focus:bg-white focus:ring-0 focus:outline-none"
                          required
                        />
                      ))}
                    </div>

                    <p className="mb-5 text-center text-xs text-gray-400">
                      Code auto-verifies when all digits are entered
                    </p>
                  </form>
                )}

                <div className="flex items-center justify-between border-t border-gray-100 pt-4">
                  <button
                    className="flex items-center gap-1 text-sm text-gray-500 transition-colors hover:text-gray-700"
                    onClick={handleBack}
                  >
                    <svg
                      className="h-3.5 w-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                    Back
                  </button>
                  <button
                    onClick={handleResendCode}
                    className="text-sm font-semibold text-gray-900 transition-colors hover:underline disabled:text-gray-400 disabled:no-underline"
                    disabled={isResending}
                  >
                    {isResending ? 'Resending...' : 'Resend Code'}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
